import { mag2, type Complex } from "./complex";

/** Escape radius squared. 16 is enough for smooth iteration coloring. */
export const ESCAPE_R2 = 16;

export type Escape = {
  n: number;
  z: Complex;
  escaped: boolean;
};

/**
 * Iterate z ↦ z² + c from z0. Returns the first iterate that leaves the
 * escape disk, or the last iterate if the orbit stayed bounded.
 */
export function iterateEscape(z0: Complex, c: Complex, maxIter: number): Escape {
  let z = z0;
  const limit = Math.max(1, Math.floor(maxIter));
  for (let n = 0; n < limit; n++) {
    if (mag2(z) > ESCAPE_R2) return { n, z, escaped: true };
    const re = z.re * z.re - z.im * z.im + c.re;
    const im = 2 * z.re * z.im + c.im;
    z = { re, im };
  }
  return { n: limit, z, escaped: false };
}

export function mandelbrotEscape(c: Complex, maxIter: number): Escape {
  return iterateEscape({ re: 0, im: 0 }, c, maxIter);
}

export function juliaEscape(z0: Complex, c: Complex, maxIter: number): Escape {
  return iterateEscape(z0, c, maxIter);
}

/** Continuous iteration count for exterior coloring. */
export function smoothIter(result: Escape): number {
  if (!result.escaped) return result.n;
  const logZn = Math.log(mag2(result.z)) / 2;
  const nu = Math.log(logZn / Math.LN2) / Math.LN2;
  return result.n + 1 - nu;
}

/**
 * The Julia set for c is connected (lace) iff c is in the Mandelbrot set,
 * i.e. the critical orbit of 0 stays bounded under z ↦ z² + c.
 */
export type JuliaKind = "lace" | "dust";

export function juliaKind(c: Complex, maxIter = 400): JuliaKind {
  return mandelbrotEscape(c, maxIter).escaped ? "dust" : "lace";
}

/** First `count` points of the critical orbit 0, c, c²+c, … */
export function criticalOrbit(c: Complex, count: number): Complex[] {
  const pts: Complex[] = [{ re: 0, im: 0 }];
  let z: Complex = { re: 0, im: 0 };
  const n = Math.max(1, count);
  for (let i = 1; i < n; i++) {
    z = { re: z.re * z.re - z.im * z.im + c.re, im: 2 * z.re * z.im + c.im };
    pts.push(z);
    if (mag2(z) > 1e6) break;
  }
  return pts;
}

/**
 * Main cardioid of the Mandelbrot set:
 * c(θ) = ½ r e^{iθ} − ¼ r² e^{2iθ}. r = 1 is the boundary.
 */
export function cardioidPoint(theta: number, radius = 1): Complex {
  const r = radius;
  const c1 = Math.cos(theta);
  const s1 = Math.sin(theta);
  const c2 = Math.cos(2 * theta);
  const s2 = Math.sin(2 * theta);
  return {
    re: 0.5 * r * c1 - 0.25 * r * r * c2,
    im: 0.5 * r * s1 - 0.25 * r * r * s2,
  };
}
